import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ThemeProvider } from '../context/ThemeContext';
import { AuthProvider } from '../context/AuthContext';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ToastProvider } from '../components/shared/Toast';
import Login from '../pages/Login';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import { Dashboard } from '../pages/Dashboard';
import { UsersPage } from '../pages/Users/UsersPage';
import { UserProfilePage } from '../pages/Users/UserProfilePage';

import RolesPage from '../pages/Roles/RolesPage';
import { AssignRolesPage } from '../pages/Roles/AssignRolesPage';
import { SettingsPage } from '../pages/Settings/SettingsPage';
import { TermsOfUsePage } from '../pages/TermsOfUse/TermsOfUsePage';
import { PrivacyPolicyPage } from '../pages/PrivacyPolicy/PrivacyPolicyPage';

import { AnalyticsPage } from '../pages/Analytics/AnalyticsPage';
import { OrganizationsPage } from '../pages/Organizations/OrganizationsPage';
import { OrganizationProfilePage } from '../pages/Organizations/OrganizationProfilePage';
import { OnboardingDataPage } from '../pages/Onboarding/OnboardingDataPage';
import { OnboardingProfilePage } from '../pages/Onboarding/OnboardingProfilePage';
import { TechTicketDashboard } from '../pages/Support/TechTicketDashboard';
import { TechTicketDetail } from '../pages/Support/TechTicketDetail';
import SupportChatPage from '../pages/Support/SupportChatPage';
import RemoteSupportPage from '../pages/Support/RemoteSupportPage';
import { EngineerRemoteSupportProvider } from '../context/EngineerRemoteSupportProvider';
import { IncidentManagementPage } from '../pages/IncidentManagement/IncidentManagementPage';
import { LicenseDetailPage } from '../pages/Licenses/LicenseDetailPage';
import { LicensesPage } from '../pages/Licenses/LicensesPage';
import { UpgradeRequestsPage } from '../pages/Licenses/UpgradeRequestsPage';

import { ProtectedRoute } from '../components/ProtectedRoute';
import { TemplateLayout } from '../components/template/Layout';
import { ProfilePage } from "../pages/ProfilePage/ProfilePage"
import { ApiDocsPage } from '../pages/ApiDocs/ApiDocsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  },
});

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ToastProvider>
              <AuthProvider>
                <EngineerRemoteSupportProvider>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <TemplateLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="organizations" element={<OrganizationsPage />} />
                    <Route path="organizations/:id" element={<OrganizationProfilePage />} />
                    <Route path="licenses" element={<LicensesPage />} />
                    <Route path="licenses/upgrade-requests" element={<UpgradeRequestsPage />} />
                    <Route path="onboarding-data" element={<OnboardingDataPage />} />
                    <Route path="onboarding-data/:type/:id" element={<OnboardingProfilePage />} />
                    <Route path="roles" element={<RolesPage />} />
                    <Route path="/users/:userId" element={<UserProfilePage />} />
                    <Route path="assign-roles" element={<AssignRolesPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="terms-of-use" element={<TermsOfUsePage />} />
                    <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="incidents" element={<IncidentManagementPage />} />
                    <Route path="support/tickets" element={<TechTicketDashboard />} />
                    <Route path="support/tickets/:id" element={<TechTicketDetail />} />
                    <Route path="support/chat" element={<SupportChatPage />} />
                    <Route path="support/remote" element={<RemoteSupportPage />} />
                    <Route path="licenses/:licenseId" element={<LicenseDetailPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="api-docs" element={<ApiDocsPage />} />
                  </Route>
                </Routes>
                </EngineerRemoteSupportProvider>
              </AuthProvider>
            </ToastProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
